import { useModeAwareQuery } from "./useModeAwareQuery";
import { useApiConfig } from "@/providers/ApiConfigProvider";
import { graphqlQuery } from "@/lib/api/graphql";
import type { DisputeUI } from "@/types/ui/disputes";
import { GlobalDataStore } from "@/lib/mock-data/global-data-store";
import type { GetDisputesParams } from "./useDisputesData";
import {
  type PaginationResult,
  type GraphQLConnection,
  pageToCursor,
  buildGraphQLSortVariable as buildSort,
  computeMockPagination,
  createDefaultPagination,
  computePaginationWithCount,
} from "@/lib/graphql-pagination";

export type { PaginationResult } from "@/lib/graphql-pagination";

// --- GraphQL types ---

interface DisputeGraphQLNode {
  disputeId: number;
  disputeNumber: string | null;
  customerId: number;
  customerName: string | null;
  customerAccountCode: string | null;
  contactId: number | null;
  contactName: string | null;
  customerContactEmail: string | null;
  contactPhone: string | null;
  disputeTypeId: number;
  disputeTypeName: string | null;
  workflowId: number | null;
  workflowName: string | null;
  amount: number;
  statusId: number;
  statusCode: string | null;
  statusName: string | null;
  priorityId: number;
  priorityCode: string | null;
  priorityName: string | null;
  assignedToUserId: number | null;
  assignedToUserName: string | null;
  dueDate: string | null;
  currentLevel: number;
  totalLevels: number;
  aiScore: number | null;
  aiRecommendation: string | null;
  description: string | null;
  resolutionNotes: string | null;
  resolutionOutcome: string | null;
  resolutionAmount: number | null;
  resolvedBy: number | null;
  resolvedByUserName: string | null;
  resolvedAtLevel: number | null;
  createdAt: string;
  updatedAt: string;
  resolvedAt: string | null;
}

interface DisputeListGraphQLResponse {
  disputes: {
    disputeList: GraphQLConnection<DisputeGraphQLNode>;
  };
}

interface DisputeCountGraphQLResponse {
  disputes: {
    disputeCount: number;
  };
}

export interface DisputeTotalsData {
  totalCount: number;
  activeCount: number;
  totalAmount: number;
  previousMonthCount: number;
  newThisWeekCount: number;
}

interface DisputeTotalsGraphQLResponse {
  disputes: {
    disputeTotals: DisputeTotalsData;
  };
}

// --- GraphQL queries ---

const DISPUTE_LIST_QUERY = `
  query DisputeList($first: Int, $after: String, $sort: [DisputeListSortSortByInput!], $filter: DisputeListFilterInput) {
    disputes {
      disputeList(first: $first, after: $after, sort: $sort, filter: $filter) {
        edges {
          node {
            disputeId
            disputeNumber
            customerId
            customerName
            customerAccountCode
            contactId
            contactName
            customerContactEmail
            contactPhone
            disputeTypeId
            disputeTypeName
            workflowId
            workflowName
            amount
            statusId
            statusCode
            statusName
            priorityId
            priorityCode
            priorityName
            assignedToUserId
            assignedToUserName
            dueDate
            currentLevel
            totalLevels
            aiScore
            aiRecommendation
            description
            resolutionNotes
            resolutionOutcome
            resolutionAmount
            resolvedBy
            resolvedByUserName
            resolvedAtLevel
            createdAt
            updatedAt
            resolvedAt
          }
        }
        pageInfo {
          startCursor
          endCursor
          hasNextPage
          hasPreviousPage
        }
      }
    }
  }
`;

const DISPUTE_COUNT_QUERY = `
  query DisputeCount($filter: DisputeListFilterInput) {
    disputes {
      disputeCount(filter: $filter)
    }
  }
`;

const DISPUTE_TOTALS_QUERY = `
  query DisputeTotals($filter: DisputeListFilterInput) {
    disputes {
      disputeTotals(filter: $filter) {
        totalCount
        activeCount
        totalAmount
        previousMonthCount
        newThisWeekCount
      }
    }
  }
`;

// --- Mapping ---

function mapGraphQLNodeToDisputeUI(node: DisputeGraphQLNode): DisputeUI {
  return {
    id: node.disputeNumber || String(node.disputeId),
    numericId: node.disputeId,
    customer: node.customerName || "",
    customerId: node.customerId,
    customerAccountCode: node.customerAccountCode || "",
    contactId: node.contactId ?? undefined,
    contactName: node.contactName || "",
    contactEmail: node.customerContactEmail || "",
    contactPhone: node.contactPhone || "",
    type: node.disputeTypeName || "",
    amount: node.amount,
    status: node.statusName || "",
    priority: node.priorityName || "",
    assignedTo: node.assignedToUserName || "",
    createdDate: node.createdAt ? new Date(node.createdAt).toISOString().split("T")[0] : "",
    dueDate: node.dueDate || "",
    workflow: node.workflowName || "",
    aiScore: node.aiScore ?? null,
    aiRecommendation: node.aiRecommendation || "",
    currentLevel: node.currentLevel,
    totalLevels: node.totalLevels,
    description: node.description || "",
    resolutionNotes: node.resolutionNotes || "",
    resolutionOutcome: (node.resolutionOutcome as DisputeUI['resolutionOutcome']) ?? null,
    resolutionAmount: node.resolutionAmount ?? null,
    resolvedBy: node.resolvedByUserName ?? null,
    resolvedAtLevel: node.resolvedAtLevel ?? undefined,
  };
}

// --- Sort / Filter builders ---

const sortFieldMap: Record<string, string> = {
  customer: "CustomerName",
  type: "DisputeTypeName",
  amount: "Amount",
  status: "StatusName",
  priority: "PriorityName",
  assignedTo: "AssignedToUserName",
  dueDate: "DueDate",
  createdAt: "CreatedAt",
  disputeId: "DisputeNumber",
};

function buildGraphQLSortVariable(sortBy?: string, sortOrder?: "asc" | "desc") {
  return buildSort(sortFieldMap, sortBy, sortOrder);
}

function buildGraphQLFilterVariable(params: GetDisputesParams) {
  const filter: Record<string, unknown> = {};

  if (params.search) {
    filter.summary = params.search;
  }
  if (params.customerId !== undefined) {
    filter.customerId = { eq: params.customerId };
  }
  if (params.status && params.status !== "all") {
    filter.statusName = { eq: params.status };
  }
  if (params.priority && params.priority !== "all") {
    filter.priorityName = { eq: params.priority };
  }
  if (params.assignedToUserId !== undefined) {
    filter.assignedToUserId = { eq: params.assignedToUserId };
  }

  return Object.keys(filter).length > 0 ? filter : undefined;
}

// --- Mock helpers ---

function getMockDisputesFromStore(): DisputeUI[] {
  const store = GlobalDataStore.getInstance();
  const mockDisputes = store.getDisputes();
  return mockDisputes.map((mockDispute: any) => ({
    id: mockDispute.id,
    numericId: mockDispute.numericId,
    customer: mockDispute.customer,
    customerId: parseInt(mockDispute.customerId),
    customerAccountCode: mockDispute.customerAccountCode || "",
    contactName: mockDispute.contactName || "",
    contactEmail: mockDispute.contactEmail || "",
    contactPhone: mockDispute.contactPhone || "",
    type: mockDispute.type,
    amount: mockDispute.amount,
    status: mockDispute.status,
    priority: mockDispute.priority,
    assignedTo: mockDispute.assignedTo,
    createdDate: mockDispute.createdDate instanceof Date
      ? mockDispute.createdDate.toISOString().split("T")[0]
      : mockDispute.createdDate,
    dueDate: mockDispute.dueDate instanceof Date
      ? mockDispute.dueDate.toISOString().split("T")[0]
      : mockDispute.dueDate,
    workflow: mockDispute.workflow,
    aiScore: mockDispute.aiScore,
    aiRecommendation: mockDispute.aiRecommendation,
    currentLevel: mockDispute.currentLevel,
    totalLevels: mockDispute.totalLevels,
    description: mockDispute.description,
    resolutionNotes: mockDispute.resolutionNotes,
  })) as DisputeUI[];
}

function filterMockDisputes(disputes: DisputeUI[], params: GetDisputesParams): DisputeUI[] {
  return disputes.filter((x) => {
    const matchesSearch = !params.search ||
      x.id.toLowerCase().includes(params.search.toLowerCase()) ||
      x.customer.toLowerCase().includes(params.search.toLowerCase()) ||
      x.type.toLowerCase().includes(params.search.toLowerCase());

    const matchesCustomer = params.customerId === undefined || x.customerId === params.customerId;
    const matchesStatus = !params.status || params.status === "all" || x.status === params.status;
    const matchesPriority = !params.priority || params.priority === "all" || x.priority === params.priority;

    // In demo mode, mock disputes use string assignedTo — skip numeric user ID filtering
    return matchesSearch && matchesCustomer && matchesStatus && matchesPriority;
  });
}

// --- Result types ---

export interface DisputesListResult {
  disputes: DisputeUI[];
  pagination: PaginationResult;
}

// --- Hooks ---

function useDisputeCountGraphQL(params?: GetDisputesParams) {
  const apiConfig = useApiConfig();
  const filter = buildGraphQLFilterVariable(params ?? {});

  return useModeAwareQuery<number, Error>(
    ["disputes-graphql-count", filter],
    async ({ signal }) => {
      const variables: Record<string, unknown> = {};
      if (filter) {
        variables.filter = filter;
      }

      const response = await graphqlQuery<DisputeCountGraphQLResponse>(
        apiConfig,
        DISPUTE_COUNT_QUERY,
        variables,
        signal,
      );

      return response.disputes.disputeCount;
    },
    0,
    {
      staleTime: 30_000,
      refetchOnMount: "always",
    }
  );
}

export function useDisputesListGraphQL(params?: GetDisputesParams) {
  const apiConfig = useApiConfig();
  const allMockDisputes = getMockDisputesFromStore();
  const queryParams: GetDisputesParams = { page: 1, pageSize: 20, sortBy: "createdAt", sortOrder: "desc", ...params };
  const mockPage = queryParams.page ?? 1;
  const mockPageSize = queryParams.pageSize ?? 20;
  const filteredMock = filterMockDisputes(allMockDisputes, queryParams);
  const mockPaged = filteredMock.slice((mockPage - 1) * mockPageSize, mockPage * mockPageSize);
  const mockPagination = computeMockPagination(filteredMock.length, mockPage, mockPageSize);
  const mockResult: DisputesListResult = { disputes: mockPaged, pagination: mockPagination };

  return useModeAwareQuery<DisputesListResult, Error>(
    ["disputes-graphql-list", queryParams],
    async ({ signal }) => {
      const page = queryParams.page ?? 1;
      const pageSize = queryParams.pageSize ?? 20;

      const variables: Record<string, unknown> = {
        first: pageSize,
        sort: buildGraphQLSortVariable(queryParams.sortBy, queryParams.sortOrder),
        filter: buildGraphQLFilterVariable(queryParams),
      };

      if (page > 1) {
        variables.after = pageToCursor(page);
      }

      const response = await graphqlQuery<DisputeListGraphQLResponse>(
        apiConfig,
        DISPUTE_LIST_QUERY,
        variables,
        signal,
      );

      const connection = response.disputes.disputeList;
      const disputes = connection.edges.map((x) => mapGraphQLNodeToDisputeUI(x.node));

      const pagination: PaginationResult = {
        page,
        pageSize,
        totalItems: disputes.length,
        totalPages: 1,
        hasNextPage: connection.pageInfo.hasNextPage,
        hasPreviousPage: connection.pageInfo.hasPreviousPage,
      };

      return { disputes, pagination };
    },
    mockResult,
    {
      staleTime: 0,
      refetchOnMount: "always",
    }
  );
}

export function useDisputeTotalsGraphQL(params?: GetDisputesParams) {
  const apiConfig = useApiConfig();
  const filter = buildGraphQLFilterVariable(params ?? {});

  const allMockDisputes = getMockDisputesFromStore();
  const filteredMock = filterMockDisputes(allMockDisputes, params ?? {});
  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const dayOfWeek = now.getDay();
  const startOfWeek = new Date(now);
  startOfWeek.setDate(now.getDate() - ((dayOfWeek + 6) % 7));
  startOfWeek.setHours(0, 0, 0, 0);

  const mockTotals: DisputeTotalsData = {
    totalCount: filteredMock.length,
    activeCount: filteredMock.filter((x) => x.status === "In Progress" || x.status === "Under Review").length,
    totalAmount: filteredMock.reduce((sum, x) => sum + x.amount, 0),
    previousMonthCount: filteredMock.filter((x) => new Date(x.createdDate) < startOfMonth).length,
    newThisWeekCount: filteredMock.filter((x) => new Date(x.createdDate) >= startOfWeek).length,
  };

  return useModeAwareQuery<DisputeTotalsData, Error>(
    ["disputes-graphql-totals", filter],
    async ({ signal }) => {
      const variables: Record<string, unknown> = {};
      if (filter) {
        variables.filter = filter;
      }

      const response = await graphqlQuery<DisputeTotalsGraphQLResponse>(
        apiConfig,
        DISPUTE_TOTALS_QUERY,
        variables,
        signal,
      );

      return response.disputes.disputeTotals;
    },
    mockTotals,
    {
      staleTime: 30_000,
      refetchOnMount: "always",
    }
  );
}

// --- Composite page data hook ---

const defaultPagination = createDefaultPagination(25);

const defaultTotals: DisputeTotalsData = {
  totalCount: 0,
  activeCount: 0,
  totalAmount: 0,
  previousMonthCount: 0,
  newThisWeekCount: 0,
};

export function useDisputesPageDataGraphQL(params?: GetDisputesParams) {
  const listQuery = useDisputesListGraphQL(params);
  const countQuery = useDisputeCountGraphQL(params);
  const totalsQuery = useDisputeTotalsGraphQL(params);

  const basePagination = listQuery.data?.pagination ?? defaultPagination;
  const totalCount = countQuery.data ?? 0;
  const pagination = computePaginationWithCount(basePagination, totalCount, params?.pageSize);

  return {
    disputes: listQuery.data?.disputes ?? [],
    pagination,
    totals: totalsQuery.data ?? defaultTotals,
    isLoading: listQuery.isLoading,
    error: listQuery.error,
    refetch: () => {
      listQuery.refetch();
      countQuery.refetch();
      totalsQuery.refetch();
    },
  };
}
